import { pad, union, type Rect } from './labels';
import type { Pt } from './worldSpec';

/**
 * Where a world map's controls stand and how far a name plate keeps from them. Pure (no Pixi), so the scene lays its
 * buttons out from these anchors and a unit test works out where a plate lands from the very same numbers. Screen pixels.
 */

/** Where the HUD starts below the top: under the app's top bar. */
export const TOP_BAR = 58;
/** How far a name plate may move to stay clear of the controls and the other names before it fades out instead. */
export const PLATE_SHIFT = 90;
/** Room kept round the controls and inside the screen's edges. */
export const CLEAR = 6;

/** The safe-area insets round the screen (a notch, the home bar). */
export interface HudInset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}
export const NO_INSET: HudInset = { top: 0, right: 0, bottom: 0, left: 0 };

/** The gap from the right and left screen edge to a button column, and between the stacked social buttons. */
const SIDE = 12;
const SOCIAL_STEP = 74;
/** The social buttons on the left: where one stands (its middle), `drop` lower where another button sits above them. */
export function socialAnchor(inset: HudInset, i: number, drop: number): Pt {
  return [inset.left + SIDE + 24, inset.top + TOP_BAR + 30 + drop + i * SOCIAL_STEP];
}
/** A world map's back button: where the first social button would stand. */
export function backAnchor(inset: HudInset): Pt {
  return [inset.left + SIDE + 24, inset.top + TOP_BAR + 30];
}
/** A world map's "All games" button: the bottom middle. */
export function listAnchor(view: { w: number; h: number }, inset: HudInset): Pt {
  return [view.w / 2, view.h - inset.bottom - 14 - 30];
}
/** A world map's crown (the gift stands 84 below it). */
export function crownAnchor(view: { w: number; h: number }, inset: HudInset): Pt {
  return [view.w - inset.right - SIDE - 30, inset.top + TOP_BAR + 34];
}
/** The zoom buttons (a fine pointer only): the `i`th of `n`, along the bottom right. */
export function zoomAnchor(view: { w: number; h: number }, inset: HudInset, i: number, n: number): Pt {
  return [view.w - inset.right - 14 - 24 - (n - 1 - i) * 62, view.h - inset.bottom - 82 - 44];
}

/** The space a bezel button covers round its middle, with its caption (read from the scene's bounds on 31 Oct 2026; the
 *  one thing here that is measured, not laid out: the buttons' sizes come from their art and text). The crown is a little larger. */
const BUTTON = { left: -26, top: -26, right: 26, bottom: 42.45 };
const CROWN = { left: -29, top: -29, right: 29, bottom: 45.45 };

function around(p: Pt, b: { left: number; top: number; right: number; bottom: number }): Rect {
  return { x: p[0] + b.left, y: p[1] + b.top, w: b.right - b.left, h: b.bottom - b.top };
}

/** What the plates keep clear of on a world map: the top bar, and each group of controls with {@link CLEAR} round it
 *  (the same as the scene's `keepOut`, which it builds from the buttons' real bounds). `fine` is a mouse or trackpad,
 *  which adds the zoom buttons. */
export function worldHudKeepOut(view: { w: number; h: number }, inset: HudInset, fine: boolean): Rect[] {
  const groups: Rect[][] = [
    [0, 1, 2].map((i) => around(socialAnchor(inset, i, SOCIAL_STEP), BUTTON)),
    [around(backAnchor(inset), BUTTON)],
    [around(listAnchor(view, inset), BUTTON)],
    [around(crownAnchor(view, inset), CROWN)],
  ];
  if (fine) groups.push([0, 1].map((i) => around(zoomAnchor(view, inset, i, 2), BUTTON)));
  return [
    { x: 0, y: 0, w: view.w, h: inset.top + TOP_BAR },
    ...groups.map((g) => pad(union(g)!, CLEAR)),
  ];
}

// ---- the overview strip (OverviewScene) ----

/** The strip's chosen-world panel (plate and Visit): its height, and a shorter one for screens under {@link COMPACT_BELOW} high. */
export const OVERVIEW_PANEL_H = 126;
export const OVERVIEW_PANEL_COMPACT_H = 98;
export const COMPACT_BELOW = 500;
/** Under this width (a phone) the strip shows its dots under the panel. */
export const DOTS_BELOW = 700;
/** The strip's bottom row: the margin under the panel (and under the phone dots), this high. */
export const BOTTOM_ROW_H = 34;
/** The panel's arrow buttons (a step west and east): this wide, smaller on a short screen, and this far from the plate, or from the
 *  Visit button on a short screen or where the screen has no room for them beside the plate. */
export const ARROW_D = 52;
export const ARROW_COMPACT_D = 44;
export const ARROW_GAP = 10;
/** The plate at its widest (the longest world's name): every world's plate is drawn this wide or less, the same for all seven. */
export const PLATE_W = 300;
/** Room kept either side of the panel for the side buttons when the arrows stand beside the plate. */
const ARROW_SIDE_ROOM = 90;
/** The dots' row: seven dots, their spacing and hit size. */
export const DOT_STEP = 26;
export const DOTS_W = 6 * DOT_STEP + 28;
export const DOTS_H = 28;

/** Where the strip's own controls stand, screen pixels: the phone dots over the bottom row, the line the panel must stay above, and
 *  where the panel's arrows go. */
export interface OverviewAnchors {
  /** The screen is short: a smaller panel. */
  compact: boolean;
  /** Dots are shown (a phone). */
  dots: boolean;
  panelH: number;
  /** The bottom row's middle line. */
  labelY: number;
  /** The panel's arrows stand beside its plate (else beside its Visit button). */
  arrowsBeside: boolean;
  /** The dots' middle line, or null without dots. */
  dotsY: number | null;
  /** The panel (plate and Visit) ends above this. */
  panelBottom: number;
}

export function overviewAnchors(view: { w: number; h: number }, inset: HudInset): OverviewAnchors {
  const compact = view.h < COMPACT_BELOW;
  const dots = view.w < DOTS_BELOW;
  const labelY = view.h - inset.bottom - (compact ? 22 : 28);
  const dotsY = dots ? labelY - (compact ? 30 : 40) : null;
  return {
    compact,
    dots,
    panelH: compact ? OVERVIEW_PANEL_COMPACT_H : OVERVIEW_PANEL_H,
    labelY,
    // A short screen keeps them beside Visit too: beside the plate they would stand on the places along its low strip.
    arrowsBeside: !compact && view.w - inset.left - inset.right >= PLATE_W + 2 * (ARROW_GAP + ARROW_D + ARROW_SIDE_ROOM),
    dotsY,
    panelBottom: dotsY !== null ? dotsY - DOTS_H / 2 - 8 : labelY - BOTTOM_ROW_H / 2 - 8,
  };
}

/** The arrows' width on a screen that is (or is not) short. */
export function arrowD(compact: boolean): number {
  return compact ? ARROW_COMPACT_D : ARROW_D;
}

/** The panel's box across, with its arrows: the plate (narrower on a narrow screen), and the arrows either side when they stand beside
 *  it (beside the Visit button they are within the plate's width). */
export function panelBoxW(view: { w: number; h: number }, inset: HudInset): number {
  const a = overviewAnchors(view, inset);
  const plate = Math.min(PLATE_W, view.w - 24);
  return a.arrowsBeside ? plate + 2 * (ARROW_GAP + arrowD(a.compact)) : plate;
}

/** What the strip's names keep clear of: the top bar, the social buttons down the left, the Wheel and gift down the right, and the
 *  dots (each with {@link CLEAR} round it). The scene builds the same from the real bounds. */
export function overviewHudKeepOut(view: { w: number; h: number }, inset: HudInset): Rect[] {
  const a = overviewAnchors(view, inset);
  const rects: Rect[][] = [
    [0, 1, 2].map((i) => around(socialAnchor(inset, i, 0), BUTTON)),
    [around(crownAnchor(view, inset), CROWN), around([crownAnchor(view, inset)[0], crownAnchor(view, inset)[1] + 84], BUTTON)],
  ];
  if (a.dotsY !== null) {
    rects.push([{ x: (view.w - DOTS_W) / 2, y: a.dotsY - DOTS_H / 2, w: DOTS_W, h: DOTS_H }]);
  }
  return [{ x: 0, y: 0, w: view.w, h: inset.top + TOP_BAR }, ...rects.map((g) => pad(union(g)!, CLEAR))];
}
